import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid, Truck } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => { document.title = 'Shop Hardware & Power Tools | Tool Supply Point'; }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) result = result.filter(p => p.categorySlug === selectedCategory);
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (slug) => { if (slug) setSearchParams({ category: slug }); else setSearchParams({}); };
  const currentCategory = categories.find(c => c.slug === selectedCategory);

  const pill = (active) => `px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${active ? 'bg-athletic-600 text-white' : 'bg-white text-dark-700 border border-dark-200 hover:border-athletic-500 hover:text-athletic-600'}`;

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Banner */}
        <section className="relative bg-dark-900 overflow-hidden">
          <img src={siteImages.shopBanner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/80 to-dark-950/40" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <nav className="text-sm text-dark-300 mb-3 uppercase tracking-wide"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-athletic-500">{currentCategory ? currentCategory.name : 'All Tools'}</span></nav>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white uppercase">{currentCategory ? currentCategory.name : 'The Full Tool Wall'}</h1>
            <p className="mt-3 text-dark-200 max-w-2xl">{currentCategory ? currentCategory.description : 'Everything from laser range finders to cordless power tools and rugged storage \u2014 hand-picked hardware that earns its place in your kit.'}</p>
          </div>
        </section>

        {/* Filter bar */}
        <div className="sticky top-[74px] lg:top-[122px] z-30 bg-white border-b border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0">
              <button onClick={() => handleCategoryChange('')} className={pill(!selectedCategory)}>All ({products.length})</button>
              {categories.map((cat) => (
                <button key={cat.id} onClick={() => handleCategoryChange(cat.slug)} className={pill(selectedCategory === cat.slug)}>{cat.name} ({cat.productCount})</button>
              ))}
            </div>
            <div className="flex items-center justify-between lg:justify-end gap-3">
              <p className="flex items-center gap-2 text-sm text-dark-600"><LayoutGrid className="w-4 h-4 text-athletic-600" /><span className="font-bold text-dark-900">{filteredProducts.length}</span> item{filteredProducts.length !== 1 ? 's' : ''}</p>
              <div className="relative">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-white border border-dark-200 text-dark-700 text-sm font-semibold rounded-sm px-4 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer">
                  <option value="featured">Featured</option><option value="price-low">Price: Low to High</option><option value="price-high">Price: High to Low</option><option value="name">Name: A-Z</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Free shipping ribbon */}
          <div className="mb-8 flex items-center gap-3 rounded-sm bg-dark-900 text-white px-5 py-4">
            <Truck className="w-5 h-5 text-athletic-500 flex-shrink-0" />
            <p className="text-sm"><span className="font-bold uppercase">Free USPS shipping</span> on every order &middot; delivered in 2&ndash;4 business days &middot; 30-day returns.</p>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="bg-dark-50 rounded-sm p-12 text-center border border-dark-200"><p className="text-dark-600">No tools found in this category.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{filteredProducts.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
